import { BreakpointObserver } from '@angular/cdk/layout';
import { Component, computed, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';

import { combineLatest, interval, Observable, Subject } from 'rxjs';
import { filter, map, startWith, take, takeUntil, tap } from 'rxjs/operators';

import { ModalsActions, ModalSize } from 'src/app/features/modals';
import { PackageUsersSelectors } from 'src/app/features/package-users';
import { LongPollingConfig } from 'src/app/features/shared/configs';

import { ProductType } from 'src/app/features/packages';
import { getProductType } from 'src/app/features/packages/store/selectors/packages.selectors';
import { CheckInActions, CheckInSelectors } from '../../store';
import { NotaryModalComponent } from '../notary-modal';
import { ButtonAction } from './button-action.enum';
import { DeviceGroupActions, DeviceGroupSelectors } from 'src/app/features/device-group';
import { MOBILE_BREAKPOINT_QUERY } from 'src/app/features/shared/constants';

@Component({
  selector: 'app-non-signing-agent-welcome-page',
  templateUrl: './non-signing-agent-welcome-page.component.html',
  styleUrls: ['./non-signing-agent-welcome-page.component.scss'],
  standalone: false,
})
export class NonSigningAgentWelcomePageComponent implements OnInit, OnDestroy {
  readonly ButtonActionEnum = ButtonAction;
  buttonAction$: Observable<ButtonAction>;
  isMobile$: Observable<boolean>;
  instructions$: Observable<string>;

  readonly shouldRenderNBTHAlertBanner = computed(
    () =>
      this.store.selectSignal(PackageUsersSelectors.selectHasNbth)() &&
      this.store.selectSignal(getProductType)() === ProductType.RemoteSigning
  );

  // using take(1) so we don't keep re-creating the participant components every time
  // the getNonSigningAgents selector emits a new value. Re-creating the components causes
  // the tab focus on the page to be lost.
  readonly nonSigningAgentsWithDisplayName$ = this.store
    .select(PackageUsersSelectors.selectNonSigningAgentsWithFullNames)
    .pipe(
      filter((nonSigningAgentsWithFullNames) => nonSigningAgentsWithFullNames.length > 0),
      take(1)
    );
  readonly isUserCheckInSequenceEmpty$ = this.store.select(
    CheckInSelectors.selectIsUserCheckInSequenceEmpty
  );
  readonly hasMultiplenonSigningAgents$ = this.store.select(
    PackageUsersSelectors.selectHasMultipleNonSigningAgents
  );
  readonly nonSaWelcomePageButtonVm$ = this.store.select(
    CheckInSelectors.selectNonSigningAgentWelcomePageButton
  );

  readonly nonSaWelcomePageRejoinButtonVm$ = this.store.select(
    CheckInSelectors.selectNonSigningAgentWelcomePageRejoinButton
  );

  pollingInterval = LongPollingConfig['6Seconds'].interval;

  private readonly onDestroyNotifier$ = new Subject();

  constructor(
    private readonly store: Store,
    private readonly breakpointObserver: BreakpointObserver
  ) {
    this.isMobile$ = this.breakpointObserver
      .observe([MOBILE_BREAKPOINT_QUERY])
      .pipe(map((result) => result.matches));
  }

  ngOnInit(): void {
    this.store.dispatch(CheckInActions.NonSigningAgentWelcomePageOpened());

    // Automatically add the non-signing agent to the check-in sequence
    // if there is only one non signing agent and they have not started check-in
    this.store
      .select(PackageUsersSelectors.getNonSigningAgents)
      .pipe(
        filter((nonSAs) => nonSAs?.every((u) => !!u?.checkInStatus)),
        filter((nonSAs) => nonSAs.length === 1 && !nonSAs[0].checkInStatus.isStarted),
        tap((nonSAs) => {
          this.store.dispatch(
            CheckInActions.SetUserCheckInSequence({ payload: [nonSAs[0].packageUserGuid] })
          );
        }),
        take(1)
      )
      .subscribe();

    combineLatest([
      this.store.select(DeviceGroupSelectors.getRejoinedPackageUsersGuid),
      this.store.select(DeviceGroupSelectors.getIsParticipantRejoining),
      this.store.select(PackageUsersSelectors.getPackageUsers),
    ])
      .pipe(
        filter(([_, __, currentPackageUsers]) => currentPackageUsers?.length > 0),
        tap(([rejoiningPackageUsers, isUserRejoining, currentPackageUsers]) => {
          const packageUserGuids = currentPackageUsers.map((p) => p?.packageUserGuid);
          const hasRejoiningUsersNotFromThisPackage = rejoiningPackageUsers.some(
            (r) => !packageUserGuids.includes(r)
          );
          if (hasRejoiningUsersNotFromThisPackage && isUserRejoining) {
            this.store.dispatch(DeviceGroupActions.ClearRejoiningUsers());
          }
        }),
        take(1)
      )
      .subscribe();

    this.instructions$ = combineLatest([this.isMobile$, this.hasMultiplenonSigningAgents$]).pipe(
      map(([isMobile, hasMultiplenonSigningAgents]) => {
        const multipleNonSAInstruction = hasMultiplenonSigningAgents
          ? 'Select everyone that will be using this device.'
          : '';
        const nonMobileInstruction = !isMobile ? "Who's here today? " : '';
        return `${nonMobileInstruction}${multipleNonSAInstruction}`;
      })
    );

    this.pollForMissingSAUpdatesOnPotentialRejoin().subscribe();
  }

  ngOnDestroy(): void {
    this.onDestroyNotifier$.next(undefined);
    this.onDestroyNotifier$.complete();
  }

  beginCheckIn() {
    this.store.dispatch(
      ModalsActions.SetStandaloneModalComponent({
        payload: {
          component: NotaryModalComponent,
          modalSize: ModalSize.fitContent,
        },
      })
    );
  }

  goToSigningRoom() {
    this.store.dispatch(CheckInActions.GoToSigningRoom());
  }

  goToWaitingRoom() {
    this.store.dispatch(CheckInActions.GoToWaitingRoom());
  }

  rejoin() {
    this.store.dispatch(CheckInActions.BeginRejoin());
  }

  pollForMissingSAUpdatesOnPotentialRejoin(): Observable<[number, boolean]> {
    return combineLatest([
      interval(this.pollingInterval).pipe(startWith(0)),
      this.store.select(CheckInSelectors.selectIsRejoiningWaitingOnSA),
    ]).pipe(
      takeUntil(this.onDestroyNotifier$),
      filter(([_, isWaitingForSA]) => isWaitingForSA),
      tap(() => {
        this.store.dispatch(CheckInActions.CheckAndUpdateIfSACompletedCheckIn());
      })
    );
  }
}
